import {
  MessageBar,
  MessageBarBody,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { useAuth, useT } from '@powerportalspro/react';
import {
  GridLayout,
  MainGrid,
  type ViewDefinition,
} from '@powerportalspro/react-fluent';

// Same vertical stack as the standalone main demo — warning bar above the
// grid, small Fluent v9 gap. Mirrors the Blazor `<FluentStack VerticalGap="10">`.
const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalMNudge,
  },
});

// Card-layout variant of `MainGridMainSample`: the same views, starting as
// cards, with the toolbar dropdown that picks automatic, table rows or cards.
// Cards have no column headers, so Sort in the toolbar's Settings menu sorts them.

const STRINGS_BASE =
  'components.PowerPortalsPro.Demo.Client.Customizations.Pages.Grids.MainGrid.MainGridCardsDemo';

const ALL_CONTACTS_VIEW_ID = '28299C6F-EBC0-4206-9E11-A373D4C9891F';
const MY_CONTACTS_VIEW_ID = '25999A64-A871-4200-A38C-22ACD531F1A2';
const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

function buildAllContactsView(): ViewDefinition {
  return {
    id: ALL_CONTACTS_VIEW_ID,
    tableName: 'contact',
    displayName: 'Active Contacts',
    fetchXml: `<fetch>
      <entity name="contact">
        <attribute name="emailaddress1" />
        <attribute name="fullname" />
        <attribute name="mobilephone" />
        <attribute name="parentcustomerid" />
        <attribute name="ownerid" />
        <attribute name="ppp_owningportaluserid" />
        <attribute name="ppp_age" />
        <attribute name="birthdate" />
        <order attribute="fullname" />
      </entity>
    </fetch>`,
    columns: [
      { columnName: 'fullname', width: 200 },
      { columnName: 'mobilephone', width: 200 },
      { columnName: 'emailaddress1', width: 225 },
      { columnName: 'parentcustomerid', width: 200 },
      { columnName: 'ppp_age', width: 100 },
      { columnName: 'birthdate', width: 125 },
    ],
  };
}

function buildMyContactsView(currentUserId: string): ViewDefinition {
  return {
    id: MY_CONTACTS_VIEW_ID,
    tableName: 'contact',
    displayName: 'My Contacts',
    fetchXml: `<fetch>
      <entity name="contact">
        <attribute name="emailaddress1" />
        <attribute name="fullname" />
        <attribute name="mobilephone" />
        <attribute name="parentcustomerid" />
        <attribute name="ownerid" />
        <attribute name="ppp_owningportaluserid" />
        <attribute name="ppp_age" />
        <filter type="or">
          <condition attribute="ppp_owningportaluserid" operator="eq" value="${currentUserId}" />
          <condition attribute="ownerid" operator="eq" value="${currentUserId}" />
        </filter>
        <order attribute="fullname" />
      </entity>
    </fetch>`,
    columns: [
      { columnName: 'fullname', width: 200 },
      { columnName: 'mobilephone', width: 200 },
      { columnName: 'emailaddress1', width: 225 },
      { columnName: 'parentcustomerid', width: 200 },
      { columnName: 'ppp_age', width: 100 },
    ],
  };
}

export function MainGridCardsSample() {
  const styles = useStyles();
  const auth = useAuth();
  const t = useT();
  const currentUserId =
    auth.status === 'authenticated' ? auth.user.userId ?? EMPTY_GUID : EMPTY_GUID;

  const customViews: ViewDefinition[] = [
    buildAllContactsView(),
    buildMyContactsView(currentUserId),
  ];

  return (
    <div className={styles.root}>
      <MessageBar
        intent="warning"
        layout="multiline"
        style={{ gridTemplateRows: 'auto', minHeight: 'auto' }}
      >
        <MessageBarBody>{t(`${STRINGS_BASE}.login-required`)}</MessageBarBody>
      </MessageBar>

      <MainGrid
        tableName="contact"
        defaultLayout={GridLayout.Cards}
        allowChangingLayout
        cardImageColumn="entityimage"
        minHeight="500px"
        maxHeight="500px"
        pageSize={10}
        customViewDefinitions={customViews}
        viewIds={[ALL_CONTACTS_VIEW_ID, MY_CONTACTS_VIEW_ID]}
        defaultViewId={ALL_CONTACTS_VIEW_ID}
      />
    </div>
  );
}
